// Wires the faceplate to the engine: controls <-> params, scopes, on-screen +
// computer + MIDI keyboards, and the sequencer transport/grid. Controls are
// bound immediately (setParam works pre-resume); audio-dependent pieces (scopes,
// MIDI) start in onStarted() once the AudioContext exists.

import {
  AudioEngine,
  isParamId,
  getDefaultSequencer,
  type ParamId,
  type PatchValue,
} from '../engine';
import { Oscilloscope } from './components/Oscilloscope';
import { Keyboard } from './components/Keyboard';
import { SequencerGrid } from './components/StepCell';
import { initMidi } from '../lib/midi-input';
import type { Knob } from './components/Knob';
import type { Slider } from './components/Slider';

// QWERTY -> semitone offset from the keyboard base (Ableton-style layout).
const COMPUTER_KEYS: Record<string, number> = {
  a: 0,
  w: 1,
  s: 2,
  e: 3,
  d: 4,
  f: 5,
  t: 6,
  g: 7,
  y: 8,
  h: 9,
  u: 10,
  j: 11,
  k: 12,
};

export interface Bindings {
  onStarted: () => Promise<void>;
}

export function initBindings(engine: AudioEngine, root: HTMLElement): Bindings {
  const controls = bindControls(engine, root);
  const grid = bindSequencer(engine, root);
  const keyboard = bindKeyboard(engine, root);
  bindComputerKeyboard(engine, keyboard);
  bindStepHighlight(engine, grid);

  // Reflect external param changes (preset load, MIDI mod wheel) back to UI.
  engine.subscribe('param', (id, value) => controls.get(id)?.(value));

  syncFromPatch(engine, controls);

  let started = false;
  const scopes: Oscilloscope[] = [];

  return {
    async onStarted() {
      if (started) return;
      started = true;

      // Scopes: one shared rAF loop drives all three.
      root.querySelectorAll<HTMLCanvasElement>('canvas[data-scope]').forEach((cv) => {
        const i = Number(cv.dataset.scope);
        const scope = new Oscilloscope(cv);
        scope.setAnalyser(engine.getAnalyser(i));
        scopes.push(scope);
      });
      const loop = (): void => {
        for (const s of scopes) s.draw();
        requestAnimationFrame(loop);
      };
      requestAnimationFrame(loop);

      // Web MIDI (after the gesture).
      const status = root.querySelector<HTMLElement>('[data-midi-status]');
      const res = await initMidi({
        noteOn: (m) => engine.noteOn(m),
        noteOff: (m) => engine.noteOff(m),
        modWheel: (v) => engine.setParam('lfo.depth', v),
      });
      if (status) status.textContent = res.message;

      syncFromPatch(engine, controls);
    },
  };
}

type Setter = (value: PatchValue) => void;

function bindControls(engine: AudioEngine, root: HTMLElement): Map<ParamId, Setter> {
  const setters = new Map<ParamId, Setter>();
  root.querySelectorAll<HTMLElement>('[data-param]').forEach((el) => {
    const id = el.dataset.param;
    if (!id || !isParamId(id)) return;
    const tag = el.tagName.toLowerCase();

    if (tag === 'synth-knob' || tag === 'synth-slider') {
      const control = el as Knob | Slider;
      el.addEventListener('input', (e) => {
        engine.setParam(id, (e as CustomEvent<number>).detail);
      });
      setters.set(id, (v) => control.setValue(Number(v)));
    } else if (tag === 'select') {
      const sel = el as HTMLSelectElement;
      sel.addEventListener('change', () => engine.setParam(id, sel.value));
      setters.set(id, (v) => {
        sel.value = String(v);
      });
    }
  });
  return setters;
}

function syncFromPatch(engine: AudioEngine, controls: Map<ParamId, Setter>): void {
  const patch = engine.getPatch();
  for (const [id, set] of controls) set(patch[id]);
}

function bindKeyboard(engine: AudioEngine, root: HTMLElement): Keyboard {
  const container = root.querySelector<HTMLElement>('[data-keyboard]')!;
  const octaveDisplay = root.querySelector<HTMLElement>('[data-octave-display]');
  const kb = new Keyboard(container, {
    onNoteOn: (m) => engine.noteOn(m),
    onNoteOff: (m) => engine.noteOff(m),
    onOctaveChange: (o) => {
      if (octaveDisplay) octaveDisplay.textContent = `Octave: ${o}`;
    },
  });
  if (octaveDisplay) octaveDisplay.textContent = `Octave: ${kb.getOctave()}`;
  return kb;
}

function bindComputerKeyboard(engine: AudioEngine, kb: Keyboard): void {
  const held = new Map<string, number>();
  const typingTarget = (t: EventTarget | null): boolean => {
    const el = t as HTMLElement | null;
    const tag = el?.tagName;
    return tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA';
  };

  window.addEventListener('keydown', (e) => {
    if (e.repeat || e.metaKey || e.ctrlKey || typingTarget(e.target)) return;
    const key = e.key.toLowerCase();
    if (key === 'z') return kb.shiftOctave(-1);
    if (key === 'x') return kb.shiftOctave(1);
    const offset = COMPUTER_KEYS[key];
    if (offset === undefined || held.has(key)) return;
    const midi = kb.baseMidi + offset;
    held.set(key, midi);
    engine.noteOn(midi);
    kb.setKeyVisual(midi, true);
    e.preventDefault();
  });

  window.addEventListener('keyup', (e) => {
    const key = e.key.toLowerCase();
    const midi = held.get(key);
    if (midi === undefined) return;
    held.delete(key);
    engine.noteOff(midi);
    kb.setKeyVisual(midi, false);
  });
}

function bindSequencer(engine: AudioEngine, root: HTMLElement): SequencerGrid {
  const gridEl = root.querySelector<HTMLElement>('[data-seq-grid]')!;
  const steps = engine.sequencer?.getState().steps ?? getDefaultSequencer().steps;
  const grid = new SequencerGrid(gridEl, steps, (i, patch) => {
    engine.sequencer?.setStep(i, patch);
  });

  const transport = root.querySelector<HTMLElement>('.transport');
  const playBtn = transport?.querySelector<HTMLButtonElement>('[data-seq="play"]');
  const stopBtn = transport?.querySelector<HTMLButtonElement>('[data-seq="stop"]');
  const tapBtn = transport?.querySelector<HTMLButtonElement>('[data-seq="tap"]');

  playBtn?.addEventListener('click', () => {
    engine.sequencer?.start();
    if (playBtn) playBtn.dataset.active = 'true';
    if (stopBtn) stopBtn.dataset.active = 'false';
  });
  stopBtn?.addEventListener('click', () => {
    engine.sequencer?.stop();
    if (playBtn) playBtn.dataset.active = 'false';
  });

  bindTapTempo(engine, tapBtn);
  return grid;
}

function bindTapTempo(engine: AudioEngine, tapBtn?: HTMLButtonElement | null): void {
  if (!tapBtn) return;
  let taps: number[] = [];
  tapBtn.addEventListener('click', () => {
    const now = performance.now();
    taps = taps.filter((t) => now - t < 2000);
    taps.push(now);
    if (taps.length >= 2) {
      const intervals = taps.slice(1).map((t, i) => t - taps[i]!);
      const avg = intervals.reduce((a, b) => a + b, 0) / intervals.length;
      // Steps are eighth notes => beat = 2 steps.
      const bpm = Math.round(60000 / (avg * 2));
      engine.setParam('seq.bpm', bpm);
    }
  });
}

function bindStepHighlight(engine: AudioEngine, grid: SequencerGrid): void {
  engine.subscribe('step', (index, when) => {
    const delay = Math.max(0, (when - engine.now()) * 1000);
    setTimeout(() => grid.highlight(index), delay);
  });
}
